import { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Bell, Mail, Calendar, CheckCircle, XCircle, Star, MessageCircle, Loader2 } from 'lucide-react';

const NOTIFICATION_TYPES = [
  { key: 'new_booking', label: 'New Booking', description: 'When a client books your service', icon: Calendar, color: 'text-blue-500 bg-blue-50' },
  { key: 'booking_confirmed', label: 'Booking Confirmed', description: 'When a booking is confirmed', icon: CheckCircle, color: 'text-green-500 bg-green-50' },
  { key: 'booking_completed', label: 'Booking Completed', description: 'When a booking is marked complete', icon: CheckCircle, color: 'text-emerald-500 bg-emerald-50' },
  { key: 'booking_cancelled', label: 'Booking Cancelled', description: 'When a booking is cancelled', icon: XCircle, color: 'text-red-500 bg-red-50' },
  { key: 'new_review', label: 'New Review', description: 'When someone leaves you a review', icon: Star, color: 'text-amber-500 bg-amber-50' },
  { key: 'review_response', label: 'Review Response', description: 'When a provider responds to your review', icon: MessageCircle, color: 'text-purple-500 bg-purple-50' },
];

export default function NotificationPreferences({ onClose }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPrefs();
  }, []);

  const fetchPrefs = async () => {
    try {
      const res = await api.get('/notifications/preferences');
      setPrefs(res.data);
    } catch (err) {
      console.error('Notification preferences fetch error:', err);
      toast.error('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (type, channel) => {
    if (!prefs) return;
    const currentValue = prefs[type]?.[channel] ?? true;
    const newValue = !currentValue;

    // Optimistic update
    setPrefs(prev => ({
      ...prev,
      [type]: { ...prev[type], [channel]: newValue },
    }));

    setSaving(true);
    try {
      await api.put('/notifications/preferences', {
        [type]: { ...prefs[type], [channel]: newValue },
      });
    } catch (err) {
      console.error('Notification preference update error:', err);
      setPrefs(prev => ({
        ...prev,
        [type]: { ...prev[type], [channel]: currentValue },
      }));
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="prefs-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div data-testid="notification-preferences">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-xl font-medium text-[#1A1A1A]">Notification Preferences</h2>
          <p className="text-sm text-stone-500 mt-0.5">Choose how you want to be notified</p>
        </div>
        {saving && <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />}
      </div>

      {/* Column headers */}
      <div className="flex items-center justify-end gap-6 mb-3 pr-1">
        <div className="flex items-center gap-1 text-xs font-medium text-stone-500 w-14 justify-center">
          <Bell className="h-3.5 w-3.5" />
          In-App
        </div>
        <div className="flex items-center gap-1 text-xs font-medium text-stone-500 w-14 justify-center">
          <Mail className="h-3.5 w-3.5" />
          Email
        </div>
      </div>

      <div className="space-y-1">
        {NOTIFICATION_TYPES.map((type) => {
          const Icon = type.icon;
          const inApp = prefs?.[type.key]?.in_app ?? true;
          const email = prefs?.[type.key]?.email ?? true;
          return (
            <div
              key={type.key}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-stone-50 transition-colors"
              data-testid={`pref-row-${type.key}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${type.color}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A1A1A]">{type.label}</p>
                <p className="text-xs text-stone-400 line-clamp-1">{type.description}</p>
              </div>
              <div className="flex items-center gap-6">
                <div className="w-14 flex justify-center">
                  <Switch
                    checked={inApp}
                    onCheckedChange={() => handleToggle(type.key, 'in_app')}
                    data-testid={`toggle-${type.key}-in_app`}
                  />
                </div>
                <div className="w-14 flex justify-center">
                  <Switch
                    checked={email}
                    onCheckedChange={() => handleToggle(type.key, 'email')}
                    data-testid={`toggle-${type.key}-email`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {onClose && (
        <div className="mt-6 pt-4 border-t border-[#E6E4DD]">
          <Button variant="outline" onClick={onClose} className="w-full border-[#E6E4DD]" data-testid="prefs-close-btn">
            Done
          </Button>
        </div>
      )}
    </div>
  );
}
